import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { TimelineOutlined } from '@mui/icons-material';
import { TABLE_FONT_FAMILY } from '../../theme';
import {
  ActivityItem,
  ActivityKind,
  CommercialDashboard,
  activityLabel,
  formatEuro,
  formatShortDate,
  splitActivities,
} from '../../utils/commercialDashboard';
import { ConsolePanel, EmptyNote } from './ConsolePanel';
import { NAUTILUS, STAGE_RAMP, useConsolePalette } from './consolePalette';

const KIND_COLORS: Record<ActivityKind, string> = {
  lead_created: STAGE_RAMP.new,
  proposal_sent: STAGE_RAMP.bidding,
  won: NAUTILUS.sand,
  lost: NAUTILUS.buoy,
  next_action: STAGE_RAMP.negotiation,
  expected_close: STAGE_RAMP.opportunity,
};

type ActivityStripProps = {
  dashboard: CommercialDashboard;
  onOpenLead: (opportunityId: number) => void;
};

const ActivityStrip: React.FC<ActivityStripProps> = ({ dashboard, onOpenLead }) => {
  const palette = useConsolePalette();
  const { past, upcoming } = splitActivities(dashboard.activities, dashboard.today);

  const renderItem = (item: ActivityItem, isPast: boolean) => {
    const label = activityLabel(item, dashboard.today);
    const overdue = label === 'Overdue action';
    const dot = overdue ? NAUTILUS.buoy : KIND_COLORS[item.kind];
    const meta = [item.client_name, item.value ? formatEuro(item.value) : null].filter(Boolean).join(' · ');
    const isToday = item.date === dashboard.today;

    return (
      <ButtonBase
        key={`${item.kind}-${item.opportunity_id}-${item.date}`}
        onClick={() => onOpenLead(item.opportunity_id)}
        sx={{
          flex: '0 0 auto',
          width: 176,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'left',
          px: 1,
          pt: 0.5,
          pb: 1,
          borderRadius: 1,
          opacity: isPast ? 0.78 : 1,
          transition: 'background-color 150ms ease-out',
          '&:hover': { bgcolor: palette.hover },
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 600, color: isToday ? palette.figure : palette.inkSoft }}
        >
          {isToday ? 'Today' : formatShortDate(item.date)}
        </Typography>
        <Box sx={{ position: 'relative', width: '100%', height: 14, my: 0.5 }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 3,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: dot,
              boxShadow: `0 0 0 3px ${palette.surface}`,
            }}
          />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: overdue ? palette.fall : palette.ink }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.8rem', color: palette.ink, width: '100%' }}>
          {item.title || 'Untitled lead'}
        </Typography>
        <Typography noWrap variant="caption" sx={{ color: palette.inkFaint, width: '100%' }}>
          {meta || '—'}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <ConsolePanel
      title="Activity"
      subtitle="Last two weeks and the month ahead, from lead records"
      icon={<TimelineOutlined />}
    >
      {past.length + upcoming.length === 0 ? (
        <EmptyNote>Nothing was logged in the last two weeks and nothing is scheduled for the next month.</EmptyNote>
      ) : (
        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 0.5, minWidth: 'max-content' }}>
            <Box
              aria-hidden
              sx={{ position: 'absolute', left: 8, right: 8, top: 31, height: '1px', bgcolor: palette.border }}
            />
            {past.map((item) => renderItem(item, true))}
            <Box
              aria-label="Today"
              sx={{
                flex: '0 0 auto',
                width: 2,
                mx: 1,
                my: 0.5,
                borderRadius: 1,
                bgcolor: palette.figure,
                opacity: 0.6,
              }}
            />
            {upcoming.map((item) => renderItem(item, false))}
          </Box>
        </Box>
      )}
    </ConsolePanel>
  );
};

export default ActivityStrip;
